/**
 * Detect and canonicalize public YouTube watch URLs so "Add by URL" can attach
 * a video instead of screenshotting the watch page.
 *
 * Only youtube.com / youtu.be / youtube-nocookie.com hosts. Video ids are the
 * standard 11-char alphabet. Query params other than `v` (timestamps, playlists)
 * are dropped so the same clip is one canonical URL for analysis + preview.
 */

const YOUTUBE_ID_RE = /^[A-Za-z0-9_-]{11}$/;

const YOUTUBE_HOSTS = new Set([
  "youtube.com",
  "www.youtube.com",
  "m.youtube.com",
  "music.youtube.com",
  "www.youtube-nocookie.com",
  "youtube-nocookie.com",
  "youtu.be",
  "www.youtu.be",
]);

export function youtubeVideoId(raw: string): string | null {
  const trimmed = (raw || "").trim();
  if (!trimmed) {
    return null;
  }
  let parsed: URL;
  try {
    parsed = new URL(/^https?:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`);
  } catch {
    return null;
  }
  const host = parsed.hostname.toLowerCase();
  if (!YOUTUBE_HOSTS.has(host)) {
    return null;
  }

  let candidate = "";
  if (host === "youtu.be" || host === "www.youtu.be") {
    candidate = parsed.pathname.replace(/^\/+/, "").split("/")[0] || "";
  } else {
    const parts = parsed.pathname.split("/").filter(Boolean);
    if (parsed.searchParams.has("v")) {
      candidate = parsed.searchParams.get("v") || "";
    } else if (parts[0] === "embed" || parts[0] === "shorts" || parts[0] === "live") {
      candidate = parts[1] || "";
    }
  }
  // IDs sometimes arrive with a leftover query glued on ("abc?t=1").
  candidate = candidate.split("?")[0].split("&")[0];
  return YOUTUBE_ID_RE.test(candidate) ? candidate : null;
}

export function isYoutubeUrl(raw: string): boolean {
  return youtubeVideoId(raw) !== null;
}

export function canonicalYoutubeWatchUrl(raw: string): string | null {
  const id = youtubeVideoId(raw);
  return id ? `https://www.youtube.com/watch?v=${id}` : null;
}

export function youtubeThumbnailUrl(videoId: string): string {
  return `https://i.ytimg.com/vi/${videoId}/hqdefault.jpg`;
}

export function youtubeEmbedUrl(videoId: string): string {
  return `https://www.youtube.com/embed/${videoId}`;
}
